import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DOCUMENT, Inject, Renderer2, Signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { RouterModule } from '@angular/router';
import { OnboardingCompletedModalComponent, OnboardingCompletedModalData } from '@rma/account-setup/public/ui-completed-modal';
import { OnboardingContextService, OnboardingCourse } from '@rma/account-setup/public-api/onboarding-service';
import { DashboardProfileContextService, DashboardUserContextService } from '@rma/authentication/public/user';
import { PaymentMethodAlertService } from '@rma/billing/public/orc-payment-method-alert';
import { CurrentSubscriptionService } from '@rma/billing/subscription-plans/public/api-subscription-features';
import { IntercomMessengerBotComponent, IntercomService } from '@rma/generic/orc/intercom';
import { RmaLetModule } from '@rma/generic/ui/let';
import { RmaModalService } from '@rma/generic/ui/modal';
import { SkipToContentComponent } from '@rma/generic/ui/skip-to-content';
import { ViewportHeightService } from '@rma/generic/util/device-size';
import { FeatureFlags, FeatureFlagService } from '@rma/generic/util/feature-flags';
import { RmaLocalStorageService } from '@rma/generic/util/local-storage';
import { nonNullable } from '@rma/generic/util/operators/non-nullable';
import { rmaShareReplay } from '@rma/generic/util/operators/rma-share-replay';
import { RouteUtilityService } from '@rma/generic/util/router-utils';
import { ThirdPartyUserIdentificationService } from '@rma/generic/util/third-party-user-identification';
import { HeaderComponent } from '@rma/layout/public/orc-header';
import { SideNavComponent } from '@rma/layout/public/orc-side-nav';
import { WelcomePrimerLoaderService } from '@rma/layout/public/orc-welcome-modal';
import { LayoutService } from '@rma/layout/public/util-layout';
import { Angulartics2GoogleGlobalSiteTag, Angulartics2GoogleTagManager } from 'angulartics2';
import {
  combineLatest,
  delay,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  pairwise,
  skip,
  startWith,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';

@Component({
  selector: 'rma-dashboard',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    HeaderComponent,
    IntercomMessengerBotComponent,
    MatButtonModule,
    MatNativeDateModule,
    MatSidenavModule,
    RmaLetModule,
    RouterModule,
    SideNavComponent,
    SkipToContentComponent,
  ],
  providers: [LayoutService, WelcomePrimerLoaderService],
  host: {
    class: 'flex flex-col w-full',
  },
})
export class DashboardComponent {
  public showHeader: Signal<boolean>;
  public showSideNav: Signal<boolean>;
  public mobileSideNavVisible: Signal<boolean>;
  public sideNavCollapsed: Signal<boolean>;

  public trialDays$: Observable<number>;
  public isSubscribed$: Observable<boolean>;
  public enableIntercom$: Observable<boolean>;

  private onboardingCourse$: Observable<OnboardingCourse | null>;

  public constructor(
    private readonly localStorage: RmaLocalStorageService,
    private readonly angulartics2GoogleTagManager: Angulartics2GoogleTagManager,
    private readonly angularticsSiteTag: Angulartics2GoogleGlobalSiteTag,
    private readonly currentSubscriptionService: CurrentSubscriptionService,
    private readonly featureService: FeatureFlagService,
    private readonly intercomService: IntercomService,
    private readonly layoutService: LayoutService,
    private readonly modalService: RmaModalService,
    private readonly onboardingContextService: OnboardingContextService,
    private readonly paymentMethodAlertService: PaymentMethodAlertService,
    private readonly routeUtils: RouteUtilityService,
    private readonly userService: DashboardUserContextService,
    private readonly profileContextService: DashboardProfileContextService,
    private readonly thirdPartyUserIdentificationService: ThirdPartyUserIdentificationService,

    @Inject(DOCUMENT) document: Document,
    welcomePrimerLoaderService: WelcomePrimerLoaderService,
    renderer: Renderer2,
    _viewportHeightService: ViewportHeightService,
  ) {
    this.initialiseThirdParty();
    this.storeUtmCampaignLocally();

    this.showHeader = this.layoutService.showHeader;
    this.showSideNav = this.layoutService.showSideNav;
    this.sideNavCollapsed = this.layoutService.desktopSideNavCollapsed;
    this.mobileSideNavVisible = this.layoutService.mobileSideNavVisible;

    this.paymentMethodAlertService.checkForAlert();

    this.trialDays$ = combineLatest([this.currentSubscriptionService.currentSubscription$]).pipe(
      map(([currentSubscription]) => {
        return currentSubscription?.trialDays ? currentSubscription?.trialDays : 0;
      }),
      startWith(0),
      rmaShareReplay(),
    );

    this.isSubscribed$ = this.currentSubscriptionService.currentSubscription$.pipe(
      map((currentSubscription) => currentSubscription.isSubscribed),
      rmaShareReplay(),
    );

    this.onboardingCourse$ = this.onboardingContextService.course$;

    this.profileContextService.profileContextChanged$.pipe(take(1)).subscribe(() => renderer.addClass(document.body, 'app-loaded'));

    welcomePrimerLoaderService.showWelcomeModal();
    this.watchOnboardingFinished();
  }

  public onNavigation() {
    this.layoutService.onNavigation();
  }

  public onToggleSideNav() {
    this.layoutService.mobileToggleSideNav();
  }

  public onToggleDesktopSideNav() {
    this.layoutService.desktopToggleSideNav();
  }

  private initialiseThirdParty() {
    this.intercomService.initialise();
    this.angulartics2GoogleTagManager.startTracking();
    this.angularticsSiteTag.startTracking();
    this.thirdPartyUserIdentificationService.initialise();
  }

  // This was originally in a separate service which depended on
  private storeUtmCampaignLocally() {
    this.routeUtils.params$
      .pipe(
        take(1),
        map((p): string | null =>
          Object.entries(p).reduce((accum, [k, v]) => (k.toLocaleLowerCase() === 'utm-campaign' ? v : accum), null),
        ),
        nonNullable(),
      )
      .subscribe((campaign: string) => {
        this.localStorage.setItem('utm-campaign', campaign);
        this.localStorage.setItem('utm-date', new Date().toString());
      });

    this.enableIntercom$ = combineLatest([
      this.featureService.getFeature$(FeatureFlags.EnableIntercom, false),
      this.userService.isImpersonating$,
    ]).pipe(map(([enabled, impersonating]) => enabled && !impersonating));
  }

  private watchOnboardingFinished() {
    const isOnboardingFinished$ = this.onboardingContextService.isCourseFinished$.pipe(distinctUntilChanged());

    // This means that the observable will complete without firing the modal when the user has previously finished the onboarding.
    const stopWatchIsFinishedWhen$ = merge(
      isOnboardingFinished$.pipe(
        filter((x) => x),
        // This delay allows the the modal to show when the user first completes the onboarding
        // Without it, the observable will complete before it can emit
        delay(100),
      ),
      this.userService.userContext$.pipe(skip(1)),
    );

    // this needs to be per user context.
    this.userService.userContext$
      .pipe(
        // We need want to delay for a second to to allow the isOnboarding a chance to re refresh
        delay(2000),
        switchMap(() => {
          return isOnboardingFinished$.pipe(
            pairwise(),
            filter(([x, y]) => !x && y),
            takeUntil(stopWatchIsFinishedWhen$),
          );
        }),
        switchMap(() => combineLatest([this.onboardingCourse$.pipe(nonNullable()), this.profileContextService.shortName$])),
        take(1),
      )
      .subscribe({
        next: ([course, shortName]) => {
          this.onboardingContextService.markCourseWithStatus(course.definition.key, 'Complete');
          this.modalService.openCelebrationFullScreenMobile<OnboardingCompletedModalComponent, OnboardingCompletedModalData>(
            OnboardingCompletedModalComponent,
            { data: { shortName } },
          );
        },
      });
  }
}
